// src/WellMonitoringPage.js
import React, { useMemo, useState, useEffect } from 'react';
import { db, storage } from './firebase.js';
import { doc, onSnapshot, collection, addDoc, query, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const READINGS_LIMIT = 400;
const CHART_HOURS = 24;

function formatStatusLabel(value) {
  if (!value) return 'Unknown';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function downsampleReadings(readings, maxPoints = 48) {
  if (readings.length <= maxPoints) return readings;
  const step = Math.ceil(readings.length / maxPoints);
  return readings.filter((_, index) => index % step === 0);
}

function dataFreshness(lastUpdated) {
  if (!lastUpdated) return { stale: true, hoursAgo: null, label: 'No data' };
  const hoursAgo = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60);
  return {
    stale: hoursAgo > 2,
    hoursAgo,
    label: hoursAgo < 1
      ? 'Just now'
      : hoursAgo < 24
        ? `${Math.round(hoursAgo)} hours ago`
        : lastUpdated.toLocaleString('en-US', {
            timeZone: 'America/New_York',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          }) + ' ET',
  };
}

const WellMonitoringPage = ({ user, isAdmin, isMobile }) => {
  const [currentData, setCurrentData] = useState({
    flowGPM: 0,
    lastUpdated: null,
    signalQuality: 'Unknown',
    serverStatus: 'Unknown',
    dailyTotalGallons: 0,
  });
  const [filterChanges, setFilterChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logDate, setLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [logNotes, setLogNotes] = useState('');
  const [logFiles, setLogFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [readings, setReadings] = useState([]);

  // Subscribe to live well data from Firestore
  useEffect(() => {
    const unsubscribeCurrent = onSnapshot(
      doc(db, 'community_well', 'current'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCurrentData({
            flowGPM: data.flowGPM ?? 0,
            lastUpdated: data.lastUpdated?.toDate?.() || new Date(),
            signalQuality: data.signalQuality || 'Unknown',
            serverStatus: data.serverStatus || 'Unknown',
            dailyTotalGallons: data.dailyTotalGallons ?? 0,
          });
        } else {
          // No data yet - show placeholder
          setCurrentData({
            flowGPM: 0,
            lastUpdated: null,
            signalQuality: 'No Data',
            serverStatus: 'No Data',
            dailyTotalGallons: 0,
          });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to well data:', err);
        setError('Failed to load live well data.');
        setLoading(false);
      }
    );

    // Listen to filter change history (most recent first)
    const unsubscribeFilters = onSnapshot(
      collection(db, 'community_well', 'current', 'filter_changes'),
      (snapshot) => {
        const changes = snapshot.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
            changedAt: d.data().changedAt?.toDate?.() || new Date(),
          }))
          .sort((a, b) => b.changedAt - a.changedAt);
        setFilterChanges(changes);
      },
      (err) => {
        console.error('Error loading filter history:', err);
      }
    );

    const unsubscribeReadings = onSnapshot(
      query(
        collection(db, 'community_well', 'current', 'readings'),
        orderBy('timestamp', 'desc'),
        limit(READINGS_LIMIT),
      ),
      (snapshot) => {
        const cutoff = Date.now() - CHART_HOURS * 60 * 60 * 1000;
        const rows = snapshot.docs
          .map((d) => {
            const data = d.data();
            const ts = data.timestamp?.toDate?.();
            if (!ts) return null;
            return {
              timestamp: ts,
              flowGPM: Number(data.flowGPM ?? 0),
            };
          })
          .filter((row) => row && row.timestamp.getTime() >= cutoff)
          .sort((a, b) => a.timestamp - b.timestamp);
        setReadings(rows);
      },
      (err) => {
        console.error('Error loading well readings:', err);
      },
    );

    return () => {
      unsubscribeCurrent();
      unsubscribeFilters();
      unsubscribeReadings();
    };
  }, []);

  const chartPoints = useMemo(() => downsampleReadings(readings), [readings]);

  const flowChartData = useMemo(() => ({
    labels: chartPoints.map((row) =>
      row.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    ),
    datasets: [
      {
        label: 'Flow (GPM)',
        data: chartPoints.map((row) => row.flowGPM),
        borderColor: '#0f766e',
        backgroundColor: 'rgba(15, 118, 110, 0.12)',
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 0,
        pointHitRadius: 8,
      },
    ],
  }), [chartPoints]);

  const flowChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${Number(ctx.parsed.y).toFixed(2)} GPM`,
        },
      },
    },
    scales: {
      x: {
        ticks: { maxTicksLimit: 8, color: '#64748b' },
        grid: { color: '#e2e8f0' },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: 'GPM', color: '#0f766e' },
        ticks: { color: '#64748b' },
        grid: { color: '#e2e8f0' },
      },
    },
  }), []);

  const freshness = dataFreshness(currentData.lastUpdated);

  const lastFilterChange = filterChanges.length > 0 ? filterChanges[0] : null;
  const daysSinceFilter = lastFilterChange
    ? Math.floor((new Date() - lastFilterChange.changedAt) / (1000 * 60 * 60 * 24))
    : null;

  let filterStatusColor = 'green';
  let filterStatusText = 'Good';
  if (daysSinceFilter !== null) {
    if (daysSinceFilter > 180) {
      filterStatusColor = 'red';
      filterStatusText = 'Overdue';
    } else if (daysSinceFilter > 120) {
      filterStatusColor = 'yellow';
      filterStatusText = 'Due Soon';
    }
  }

  const handleOpenLogModal = () => {
    setLogDate(new Date().toISOString().split('T')[0]);
    setLogNotes('');
    setLogFiles([]);
    setShowLogModal(true);
  };

  const handleLogFilterChange = async () => {
    if (!user || !isAdmin) return;
    setSaving(true);
    setError(null);

    try {
      const photoUrls = [];

      // Upload any photos
      for (const file of logFiles) {
        const storageRef = ref(
          storage,
          `community_well/filter_changes/${Date.now()}_${file.name}`
        );
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        photoUrls.push(url);
      }

      // Save filter change record
      await addDoc(collection(db, 'community_well', 'current', 'filter_changes'), {
        changedAt: new Date(logDate),
        changedBy: user.uid,
        notes: logNotes.trim() || 'Filter changed',
        photos: photoUrls,
        createdAt: serverTimestamp(),
      });

      setShowLogModal(false);
      setLogNotes('');
      setLogFiles([]);
    } catch (err) {
      console.error('Error logging filter change:', err);
      setError('Failed to save filter change. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusDot = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('strong') || s.includes('connected') || s.includes('good')) {
      return <span className="well-status-dot well-status-green" />;
    }
    if (s.includes('weak') || s.includes('connecting')) {
      return <span className="well-status-dot well-status-yellow" />;
    }
    return <span className="well-status-dot well-status-red" />;
  };

  if (loading) {
    return (
      <div className={`well-page ${isMobile ? 'well-page-mobile' : ''}`}>
        <h2 className="text-2xl font-bold mb-6">Well Monitoring</h2>
        <p className="text-gray-600">Loading well data...</p>
      </div>
    );
  }

  return (
    <div className={`well-page ${isMobile ? 'well-page-mobile' : ''}`}>
      <h2 className="text-2xl font-bold mb-2">Community Well Monitoring</h2>
      <p className="text-gray-600 mb-6">
        Live data from the Hydrific Droplet sensor on the community well supply line.
      </p>

      {error && <div className="text-red-600 mb-4">{error}</div>}

      {freshness.stale ? (
        <div className="well-stale-banner mb-4" role="alert">
          Sensor bridge offline — last sync {freshness.label}. The usage figure below is from that time, not live today.
        </div>
      ) : null}

      {/* Current Flow Rate - Hero Card */}
      <div className="well-stat-card mb-6" style={{ maxWidth: 420 }}>
        <div className="flex items-baseline gap-3">
          <div className="well-big-number">
            {currentData.flowGPM.toFixed(1)}
          </div>
          <div>
            <span className="text-2xl font-semibold text-gray-700">GPM</span>
            <div className="text-sm text-gray-500">Current Flow Rate</div>
          </div>
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Last updated:{' '}
          {currentData.lastUpdated
            ? currentData.lastUpdated.toLocaleTimeString()
            : 'No data yet'}
        </div>
      </div>

      {/* Status Grid */}
      <div className="well-stats-grid mb-8">
        <div className="well-stat-card well-usage-card">
          <div className="well-label">{freshness.stale ? 'Last Known Daily Total' : "Today's Total Usage"}</div>
          <div className="well-usage-value">
            {currentData.dailyTotalGallons.toLocaleString(undefined, { maximumFractionDigits: 1 })}
            <span className="well-usage-unit">gal</span>
          </div>
          {freshness.stale ? (
            <div className="well-usage-stale-note">Frozen since {freshness.label}</div>
          ) : null}
        </div>

        <div className="well-stat-card">
          <div className="well-label">Sensor Signal</div>
          <div className="well-status-row">
            {getStatusDot(currentData.signalQuality)}
            <span className="font-medium">{formatStatusLabel(currentData.signalQuality)}</span>
          </div>
        </div>

        <div className="well-stat-card">
          <div className="well-label">Cloud Connection</div>
          <div className="well-status-row">
            {getStatusDot(currentData.serverStatus)}
            <span className="font-medium">{formatStatusLabel(currentData.serverStatus)}</span>
          </div>
        </div>
      </div>

      {/* 24-hour flow chart */}
      <div className="well-stat-card mb-8">
        <div className="well-label">24-Hour Flow Rate</div>
        {chartPoints.length > 1 ? (
          <div className="well-chart-wrap">
            <Line data={flowChartData} options={flowChartOptions} />
          </div>
        ) : (
          <p className="text-gray-500 mt-3">Waiting for flow readings from the sensor bridge…</p>
        )}
      </div>

      {/* Filter Status Section */}
      <div className={`well-stat-card filter-status-card mb-8 ${filterStatusColor === 'red' ? 'filter-overdue' : filterStatusColor === 'yellow' ? 'filter-warning' : ''}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-gray-600">FILTER STATUS</div>
            <div className="text-2xl font-semibold mt-1">
              {lastFilterChange ? (
                <>
                  Last changed: <span className="text-gray-800">{formatDate(lastFilterChange.changedAt)}</span>
                  <span className="ml-2 text-base font-normal text-gray-500">
                    ({daysSinceFilter} days ago)
                  </span>
                </>
              ) : (
                'No filter change records yet'
              )}
            </div>
            <div className="mt-1">
              Status: <span className={`font-semibold ${filterStatusColor === 'red' ? 'text-red-600' : filterStatusColor === 'yellow' ? 'text-yellow-600' : 'text-green-600'}`}>
                {filterStatusText}
              </span>
            </div>
            {lastFilterChange?.notes && (
              <div className="mt-2 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                {lastFilterChange.notes}
              </div>
            )}
          </div>

          {user && isAdmin && (
            <button
              onClick={handleOpenLogModal}
              className="header-nav-button"
              style={{ maxWidth: '200px', background: '#0f766e', color: 'white' }}
            >
              Log New Filter Change
            </button>
          )}
        </div>
      </div>

      {/* Filter Change History */}
      {filterChanges.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-3">Filter Change History</h3>
          <div className="space-y-3">
            {filterChanges.slice(0, 6).map((change) => (
              <div key={change.id} className="well-stat-card">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{formatDate(change.changedAt)}</div>
                    <div className="text-sm text-gray-600 mt-1">{change.notes}</div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {change.photos?.length > 0 ? `${change.photos.length} photo(s)` : ''}
                  </div>
                </div>
                {change.photos && change.photos.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {change.photos.map((url, idx) => (
                      <img
                        key={idx}
                        src={url}
                        alt="Filter"
                        className="w-20 h-20 object-cover rounded border"
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Source Note */}
      <div className="text-xs text-gray-500 mt-8">
        Data is provided by the Hydrific Droplet sensor via local Smart Home API (MQTT).
        Live readings update automatically when the bridge is running.
      </div>

      {/* Log Filter Change Modal */}
      {showLogModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <h2 className="modal-title">Log Filter Change</h2>

            <label className="block mb-1 text-sm font-medium">Date Changed</label>
            <input
              type="date"
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
              className="modal-input mb-4"
            />

            <label className="block mb-1 text-sm font-medium">Notes</label>
            <textarea
              value={logNotes}
              onChange={(e) => setLogNotes(e.target.value)}
              placeholder="e.g. Replaced 5-micron sediment filter and carbon block"
              className="modal-textarea mb-4"
              rows={4}
            />

            <label className="block mb-1 text-sm font-medium">Photos (optional)</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setLogFiles(Array.from(e.target.files))}
              className="mb-4"
            />

            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

            <div className="modal-button-group">
              <button
                onClick={handleLogFilterChange}
                disabled={saving}
                className="modal-button modal-button-primary"
              >
                {saving ? 'Saving...' : 'Save Filter Change'}
              </button>
              <button
                onClick={() => setShowLogModal(false)}
                disabled={saving}
                className="modal-button modal-button-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WellMonitoringPage;
